import { type AcquireBookStatus } from "../types";
import { classifyErrorBadge } from "./errorClassifier";
import {
  mergeRowsByProperty,
  type AcquireBookStatusCellMeta,
  type AcquireBookStatusDataGridRow,
  type RowAggregationOptions,
} from "./mergeRowsByProperty";
import { normalizeTextForSearch } from "./normalizeTextForSearch";

type AcquireBookStatusUriFields = Pick<
  AcquireBookStatusDataGridRow,
  | "allMattersInfo"
  | "allMattersDetailId"
  | "allMattersIsOtherUser"
  | "allMattersInfoMeta"
  | "allMattersInfoDate"
  | "ownerInfo"
  | "ownerInfoDetailId"
  | "ownerInfoIsOtherUser"
  | "ownerInfoMeta"
  | "ownerInfoDate"
  | "electricDrawings"
  | "electricDrawingsMeta"
  | "electricDrawingsDate"
  | "tisekiDrawings"
  | "tisekiDrawingsMeta"
  | "tisekiDrawingsDate"
  | "tiekiDrawings"
  | "tiekiDrawingsMeta"
  | "tiekiDrawingsDate"
  | "buildingDrawings"
  | "buildingDrawingsMeta"
  | "buildingDrawingsDate"
  | "errorTypes"
>;

// statusの状態に応じてDataGridのセルに格納する値を決める
const getUriIfSuccess = (value: AcquireBookStatus): string => {
  if (value.status === "SUCCEEDED") {
    return value.uri ?? "";
  } else if (value.status === "FAILED") {
    return "FAILED";
  } else {
    return "PROCESSING";
  }
};

const createErrorMeta = (
  value: AcquireBookStatus
): AcquireBookStatusCellMeta | null => {
  if (value.status !== "FAILED") return null;
  const badge = classifyErrorBadge(value.errorDetail, value.errorCode);
  return {
    badge,
    detail: value.errorDetail,
  };
};

// pictureTypeやoptionsの状態に応じて適切な項目にURIを格納する
const distributeUris = (
  value: AcquireBookStatus
): AcquireBookStatusUriFields => {
  const receivedDate =
    value.pictureReceivedDate !== null
      ? new Date(value.pictureReceivedDate)
      : null;
  const result: AcquireBookStatusUriFields = {
    allMattersInfo: "",
    allMattersDetailId: -1,
    // detailId が -1（未設定）の間はフラグの値は使われないため初期値は何でもよい
    allMattersIsOtherUser: false,
    allMattersInfoMeta: null,
    allMattersInfoDate: null,
    ownerInfo: "",
    ownerInfoDetailId: -1,
    ownerInfoIsOtherUser: false,
    ownerInfoMeta: null,
    ownerInfoDate: null,
    electricDrawings: "",
    electricDrawingsMeta: null,
    electricDrawingsDate: null,
    tisekiDrawings: "",
    tisekiDrawingsMeta: null,
    tisekiDrawingsDate: null,
    tiekiDrawings: "",
    tiekiDrawingsMeta: null,
    tiekiDrawingsDate: null,
    buildingDrawings: "",
    buildingDrawingsMeta: null,
    buildingDrawingsDate: null,
    errorTypes: [],
  };

  switch (value.pictureType) {
    case "SHOYUSHAJIKO":
      result.ownerInfoDetailId = value.id;
      result.ownerInfo = getUriIfSuccess(value);
      // 同一物件で別ユーザーの全部事項とマージされた際に正しい所有権で
      // 連携対象を判定できるよう、明細レベルのフラグを保持する
      result.ownerInfoIsOtherUser = value.isOtherUserResource;
      result.ownerInfoMeta = createErrorMeta(value);
      result.ownerInfoDate = receivedDate;
      break;
    case "CHISEKISOKURYOZU":
      result.tisekiDrawings = getUriIfSuccess(value);
      result.tisekiDrawingsMeta = createErrorMeta(value);
      result.tisekiDrawingsDate = receivedDate;
      break;
    case "DENSHIKOZU":
      result.electricDrawings = getUriIfSuccess(value);
      result.electricDrawingsMeta = createErrorMeta(value);
      result.electricDrawingsDate = receivedDate;
      break;
    case "CHIEKIKENZUMEN":
      result.tiekiDrawings = getUriIfSuccess(value);
      result.tiekiDrawingsMeta = createErrorMeta(value);
      result.tiekiDrawingsDate = receivedDate;
      break;
    case "TATEMONOZUMEN":
      result.buildingDrawings = getUriIfSuccess(value);
      result.buildingDrawingsMeta = createErrorMeta(value);
      result.buildingDrawingsDate = receivedDate;
      break;
    case "ZENBUJIKO":
      result.allMattersDetailId = value.id;
      result.allMattersInfo = getUriIfSuccess(value);
      result.allMattersIsOtherUser = value.isOtherUserResource;
      result.allMattersInfoMeta = createErrorMeta(value);
      result.allMattersInfoDate = receivedDate;
      break;
    default:
      break;
  }
  return result;
};

export const convertRows = (
  rows: AcquireBookStatus[] | undefined,
  options: RowAggregationOptions = {}
): AcquireBookStatusDataGridRow[] => {
  const { collectsUniqueIds = false } = options;
  if (rows === undefined) return [];

  const result = rows.map((value): AcquireBookStatusDataGridRow => {
    return {
      id: value.id,
      // Salesforce連携でしか使わないため、連携を使わない利用者には集めない
      uniqueIds: collectsUniqueIds ? [value.uniqueId] : [],
      isOtherUserResource: value.isOtherUserResource,
      companyName: value.companyName ?? "",
      realEstateType: value.realEstateType,
      prefectureName: value.prefectureName,
      location: value.location,
      normalizedLocation: normalizeTextForSearch(value.location),
      chibanKaokuNumber: value.chibanKaokuNumber,
      normalizedChiban: normalizeTextForSearch(value.chibanKaokuNumber),
      normalizedFreeWord: normalizeTextForSearch(
        `${value.prefectureName}${value.location}${value.chibanKaokuNumber}`
      ),
      pictureReceivedDate:
        value.pictureReceivedDate !== null
          ? new Date(value.pictureReceivedDate)
          : null,
      status: typeof value.status === "string" ? value.status : "",
      ...distributeUris(value),
    };
  });
  return mergeRowsByProperty(result, { collectsUniqueIds });
};
